import { FC, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, withStyles, FedwireTransactionResponse, Card, capitalizeFirstLetter } from 'vopay';
import { TransactionCard, Title, DetailsRows, ExpandableText, TransactionStatus } from 'UI';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import useTransactionApproval from './useTransactionApproval';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: FedwireTransactionResponse;
  Notes: string;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

const FedwireWithdrawalTransactionInfo: FC<TransactionInfoProps> = ({ transaction, Notes }) => {
  const toast = useToast();
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.FedwireWithdrawalTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();

  const isApprovalRequested = useMemo(() => {
    return allApprovalTransactions.some((t) => t.ID.toString() === transaction.TransactionID);
  }, [transaction.TransactionID, allApprovalTransactions]);
  const receiverName = transaction.FirstName ? transaction.FirstName + transaction.LastName : transaction.CompanyName;

  useEffect(() => {
    if (!isApprovalRequested) return;

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transaction.TransactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [isApprovalRequested]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.Fedwire Withdrawal', {
        defaultValue: 'Fedwire Withdrawal',
      }),
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.wireReferenceNumber', { defaultValue: 'Wire Reference Number' }),
      value: transaction.WireReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
  ];

  const approvalHistoryDetails =
    approvalHistory.length > 0
      ? approvalHistory.flatMap((item) => [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: item.Username,
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: (
              <Box flx="col" gapSize="11px" ai="fe">
                <TransactionStatus
                  status={item.ApprovalDecision.toLowerCase()}
                  label={capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                      defaultValue: item.ApprovalDecision,
                    }),
                  )}
                />
                <div>{item.DecisionDate}</div>
              </Box>
            ),
          },
          {
            label: t('ApprovalHistoryDetails.reason', { defaultValue: 'Reason' }),
            value: <ExpandableText text={item.Reason || ''} />,
            visible: item.Reason !== null,
          },
        ])
      : [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: '-',
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: '-',
          },
        ];

  const recipientDetails = [
    {
      label: t('AccountHolderDetails.name', { defaultValue: 'Name' }),
      value: `${transaction.FirstName || ''} ${transaction.LastName || ''}`,
    },
    {
      label: t('AccountHolderDetails.address', { defaultValue: 'Address' }),
      value: transaction.Address1 || transaction.Address2 || '-',
    },
    {
      label: t('AccountHolderDetails.city', { defaultValue: 'City' }),
      value: transaction.City || '-',
    },
    {
      label: t('AccountHolderDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction['PostalCode/Zipcode'] || '-',
    },
    {
      label: t('AccountHolderDetails.provinceState', { defaultValue: 'Province/State' }),
      value: transaction['Province/State'] || '-',
    },
    {
      label: t('AccountHolderDetails.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
  ];

  const bankAccountDetails = [
    {
      label: t('BankAccountDetails.abaRoutingNumber', { defaultValue: 'ABA Routing Number' }),
      value: transaction.ABARoutingNumber || '-',
    },
    {
      label: t('BankAccountDetails.accountNumber', { defaultValue: 'Account Number' }),
      value: transaction.AccountNumber.replaceAll('*', '•') || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={receiverName || ''}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={'Fedwire Withdrawal'}
        transactionDate={transaction.TransactionDateTime}
        lastModified={transaction.LastModified}
        failureReason={state.failureReason}
        isApprovalRequested={isApprovalRequested}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsHeader')}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {isApprovalRequested && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('ApprovalHistoryDetails.title', { defaultValue: 'Approval History' })}
          </Title>
          <DetailsRows className={cn('rows')} details={approvalHistoryDetails} />
        </Card>
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientInformationHeader')}
        </Title>
        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('bankAccountDetailsHeader')}
        </Title>
        <DetailsRows className={cn('rows')} details={bankAccountDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoHeader')}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default FedwireWithdrawalTransactionInfo;
